import { Box, Checkbox, IconButton } from '@mui/material';
import { ChangeEvent } from 'react';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Locale } from '@synergy/models';
import {
    PALO_CELL_FIELD,
    PALO_CELL_MISSING,
    PaloPositionColumn,
    PaloPositionTable,
    PaloQuantityCell,
} from '@synergy/components-palo';
import { PlusCircleIcon } from '@synergy/theme';
import { PoolButton, PoolTextArea, PoolTextField, ProductDetails } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { getCurrencySymbol } from '@synergy/utils';

import { FormValues } from '../models/form-values';
import { Offer } from '../../../common/models/offer';
import { OfferItem } from '../../../common/models/offer-item';
import { OffersTableFooter } from './offers-table-footer';

const styles = () =>
    createStyles({
        container: {
            marginTop: 54,
            marginBottom: 41,
        },
        inputPositionCost: {
            '& > div > input': {
                textAlign: 'right',
            },
        },
        contentQuantity: {
            fontSize: 12,
        },

        notOffered: {
            opacity: 0.4,
        },
        circleIcon: {
            transform: 'rotate(45deg)',
        },
        circleButton: {
            marginTop: 10,
        },
        alternativeContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: 23,
            marginTop: 8,
        },
        alternativeButton: {
            paddingLeft: 3,
            marginBottom: 12,
        },
    });

interface OffersTableProps extends WithStyles<typeof styles> {
    locale: Locale;
    currency: string;
    itemIndex: number;
    offer: Offer;
    readonly: boolean;
    onSelectionChange: (selectedItems: number[]) => void;
}

interface OffersTableState {
    selectedItems: number[];
}

class OffersTableBase extends FormComponent<FormValues, OffersTableProps, OffersTableState> {
    public override state: OffersTableState = {
        selectedItems: [],
    };

    /**
     * The columns of the shared position table (LST-S17): what the column
     * holds decides its width, so a quantity here is as wide as a quantity in
     * the admin's own order mask.
     *
     * The first one is the supplier's answer — whether they offer this
     * position at all. It is a value of the offer, not a way of picking rows
     * out, so it stands in a column of its own rather than in the table's
     * selection box.
     */
    private columns = (form: FormikProps<FormValues>): PaloPositionColumn<OfferItem>[] => [
        {
            key: 'offered',
            type: 'flag',
            // The box needs to say what it does. Without a heading suppliers
            // did not see that a position can be left out and typed 0 € into
            // the price instead, which the check then refused (GF 13.10.2026,
            // #2239 R-140).
            label: <FormattedMessage id="app.offers.table.offered" />,
            render: item => (
                <Checkbox
                    size="small"
                    checked={!!item.id && this.state.selectedItems.includes(item.id)}
                    disabled={this.props.readonly || !item.id}
                    onChange={event => this.toggleOffered(form, item, event.target.checked)}
                />
            ),
        },
        {
            key: 'name',
            type: 'name',
            label: <FormattedMessage id={'app.table.column.article'} />,
            render: (item, index) => this.renderArticle(item, index),
        },
        {
            key: 'quantity',
            type: 'quantity',
            label: <FormattedMessage id={'app.table.column.quantity'} />,
            render: item => this.renderQuantity(item.quantity, item.orderUnit),
        },
        {
            key: 'contentQuantity',
            type: 'quantity',
            label: <FormattedMessage id={'app.table.column.contentQuantity'} />,
            render: item => this.renderQuantity(item.contentQuantity ?? '-', item.contentUnit),
        },
        {
            key: 'price',
            type: 'price',
            label: (
                <FormattedMessage
                    id={'app.table.column.price'}
                    values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                />
            ),
            render: (item, index) => this.renderPrice(item, index),
        },
        {
            key: 'positionCost',
            type: 'amount',
            label: (
                <FormattedMessage
                    id={'app.table.column.positionValue'}
                    values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                />
            ),
            render: item => this.renderPositionCost(item),
        },
    ];

    /** Takes a position into the offer or out of it, and keeps the form in step. */
    private toggleOffered = (form: FormikProps<FormValues>, item: OfferItem, offered: boolean): void => {
        if (!item.id) {
            return;
        }

        const next = offered
            ? [...this.state.selectedItems, item.id]
            : this.state.selectedItems.filter(id => id !== item.id);

        this.handleSelectionChange(next, form);
    };

    private handlePriceChanged =
        (form: FormikProps<FormValues>, prefix: string, index: number) => (event: ChangeEvent<HTMLInputElement>) => {
            // store price
            const price = Number.parseFloat(event.target.value);
            form.setFieldValue(`${prefix}price`, price);
            // set contentPrice
            const contentQuantity = form.values.items[this.props.itemIndex].offer.offerItems[index].contentQuantity;
            if (contentQuantity && contentQuantity > 0) {
                form.setFieldValue(`${prefix}contentQuantityPrice`, price / contentQuantity);
            }
        };

    private handleAlternativeChange = (
        form: FormikProps<FormValues>,
        fieldName: string,
        prefix: string,
        index: number
    ) => {
        form.setFieldValue(fieldName, false);
        form.setFieldValue(`${prefix}offerItems.${index}.alternativeNote`, '');
    };

    private handleSelectionChange = (selectedItems: number[], form: FormikProps<FormValues>) => {
        const prefix = `items.${this.props.itemIndex}.offer.`;

        this.setState({ selectedItems });

        this.props.onSelectionChange(selectedItems);
        form.values.items[this.props.itemIndex].offer.offerItems.map((item, index) =>
            form.setFieldValue(
                `${prefix}offerItems.${index}.isSelected`,
                !!(item.id && selectedItems.includes(item.id))
            )
        );
    };

    public override componentDidMount() {
        const selectedItems = this.props.offer.offerItems
            .filter(offerItem => (offerItem.isOffered || offerItem.isAlternative) && offerItem.id)
            .map(item => item.id ?? -1); // id -1 will never occur because we filter offerItem.id before map

        this.setState({ selectedItems });
        this.props.onSelectionChange(selectedItems);
    }

    /** Whether the supplier may still write in this row. */
    private isDisabled = (item: OfferItem): boolean =>
        this.props.readonly || !item.id || !this.state.selectedItems.includes(item.id);

    private renderArticle = (offerItem: OfferItem, index: number) => {
        const { classes } = this.props;
        const prefix = `items.${this.props.itemIndex}.offer.`;
        const disabled = this.isDisabled(offerItem);

        return (
            <>
                <ProductDetails className={offerItem.isOffered ? '' : classes.notOffered} product={offerItem} />

                {offerItem.isAlternative ? (
                    <div className={classes.alternativeContainer}>
                        <Field name={`${prefix}offerItems.${index}.alternativeNote`}>
                            {({ field, form }: FieldProps<string>) => (
                                // Rows of its own, or it takes the whole height of the
                                // cell (#2326 R-247).
                                <PoolTextArea
                                    id="alternativeNote"
                                    fullWidth={true}
                                    label={<FormattedMessage id="app.label.alternativeNote" />}
                                    error={this.hasError(form, field.name)}
                                    helperText={this.getError(form, field.name)}
                                    readOnly={disabled}
                                    rows={3}
                                    maxRows={8}
                                    {...field}
                                />
                            )}
                        </Field>
                        <Field name={`${prefix}offerItems.${index}.isAlternative`}>
                            {({ field, form }: FieldProps<boolean, FormValues>) => (
                                <IconButton
                                    onClick={() => this.handleAlternativeChange(form, field.name, prefix, index)}
                                    classes={{ root: classes.circleButton }}
                                    disabled={disabled}
                                >
                                    <PlusCircleIcon
                                        sx={{
                                            color: theme =>
                                                disabled ? theme.palette.grey[500] : theme.palette.primary.main,
                                        }}
                                        className={classes.circleIcon}
                                    />
                                </IconButton>
                            )}
                        </Field>
                    </div>
                ) : (
                    <Field name={`${prefix}offerItems.${index}.isAlternative`}>
                        {({ field, form }: FieldProps<boolean>) => (
                            <PoolButton
                                startIcon={
                                    <PlusCircleIcon
                                        sx={{
                                            color: theme =>
                                                disabled ? theme.palette.grey[500] : theme.palette.primary.main,
                                        }}
                                    />
                                }
                                onClick={() => form.setFieldValue(field.name, true)}
                                className={classes.alternativeButton}
                                disabled={disabled}
                            >
                                <FormattedMessage id="app.label.alternativeProdukt" />
                            </PoolButton>
                        )}
                    </Field>
                )}
            </>
        );
    };

    /**
     * The figure and its unit, in the two fixed boxes every quantity in PALO
     * uses (LST-S1a). The unit used to sit inside the field as an adornment,
     * which made the field as wide as the longest unit in the column.
     */
    // Text, not a disabled field: greyed out at 2.9:1 the requested quantity,
    // the figure a supplier offers against, read as unavailable (#2329 R-255).
    private renderQuantity = (value: number | string, unit?: string) => (
        <PaloQuantityCell unit={unit}>
            <Box
                component="span"
                sx={{
                    display: 'block',
                    textAlign: 'right',
                    color: 'text.primary',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {value}
            </Box>
        </PaloQuantityCell>
    );

    private renderPrice = (offerItem: OfferItem, index: number) => {
        const prefix = `items.${this.props.itemIndex}.offer.`;

        return (
            <Field name={`${prefix}offerItems.${index}.price`}>
                {({ field, form }: FieldProps<FormValues>) => (
                    <PoolTextField
                        id="positionCost"
                        fieldType="dense"
                        className={this.priceClassName(offerItem, field.value)}
                        error={this.hasError(form, field.name)}
                        helperText={this.getError(form, field.name)}
                        readOnly={this.isDisabled(offerItem)}
                        {...field}
                        /*
                         * The price is nullable, and a position nobody has
                         * answered arrives without one — of the 357.594 offer
                         * positions on record, 164.579 carry no price at all.
                         * So the empty field needs no help: it is empty
                         * because there is nothing there.
                         *
                         * Hiding a zero on top of that cost two attempts at
                         * #2239 R-140. The field is re-rendered while it is
                         * being typed in, and any rule of the form "show the
                         * 0 only while the cursor is in here" loses that race:
                         * the 0 vanished under the supplier's hands and the
                         * check then called the field empty rather than zero.
                         * A typed 0 now simply stays, and the rule the GF set
                         * — a price must be above 0, otherwise deselect the
                         * position — is what answers it.
                         */
                        value={field.value ?? ''}
                        onChange={this.handlePriceChanged(form, `${prefix}offerItems.${index}.`, index)}
                        isNumeric={true}
                        minDecimalScale={2}
                        decimalScale={4}
                        placeholder={(0.0).toLocaleString(this.props.locale.replace('_', '-'), {
                            minimumFractionDigits: 2,
                        })}
                    />
                )}
            </Field>
        );
    };

    /**
     * An offered position is waiting for its price: it is drawn as a field, and
     * marked while there is none. A 0 counts as none — a price has to be above
     * it (#2335).
     */
    private priceClassName = (offerItem: OfferItem, price: unknown): string => {
        const classes = [this.props.classes.inputPositionCost];

        if (!this.isDisabled(offerItem)) {
            classes.push(PALO_CELL_FIELD);

            if (null === price || undefined === price || '' === price || 0 === Number(price)) {
                classes.push(PALO_CELL_MISSING);
            }
        }

        return classes.join(' ');
    };

    private renderPositionCost = (offerItem: OfferItem) => {
        const offered = !!offerItem.id && this.state.selectedItems.includes(offerItem.id);
        const positionTotal = offered ? offerItem.quantity * (offerItem.price || 0) : 0;
        const contentQuantityPrice =
            offerItem.isOffered || offerItem.isAlternative ? offerItem.contentQuantityPrice || 0 : 0;

        return (
            <div>
                <FormattedNumber
                    value={positionTotal}
                    style="decimal"
                    minimumFractionDigits={2}
                    maximumFractionDigits={4}
                />
                {offerItem.contentQuantityPrice && (
                    <div className={this.props.classes.contentQuantity}>
                        <FormattedNumber
                            value={contentQuantityPrice}
                            style="decimal"
                            minimumFractionDigits={2}
                            maximumFractionDigits={4}
                        />
                        &nbsp;/&nbsp;
                        {offerItem.contentUnit}
                    </div>
                )}
            </div>
        );
    };

    public override render() {
        const { classes, offer, currency, itemIndex, readonly } = this.props;

        return (
            <div className={classes.container}>
                <Field>
                    {({ form }: FieldProps<FormValues>) => (
                        <PaloPositionTable<OfferItem>
                            items={offer.offerItems}
                            columns={this.columns(form)}
                            getRowKey={(item, index) => item.id ?? index}
                            // The supplier answers an inquiry; they do not add,
                            // move or remove its positions. The fields in the
                            // cells stay open — this is about the table itself.
                            readonly={true}
                        />
                    )}
                </Field>
                <OffersTableFooter
                    offer={offer}
                    currency={currency}
                    itemIndex={itemIndex}
                    readonly={readonly}
                    selectedOfferItems={this.state.selectedItems}
                />
            </div>
        );
    }
}

export const OffersTable = withStyles(styles)(OffersTableBase);
